import { FileArrowUp, UploadSimpleIcon } from "@phosphor-icons/react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface ImportMetersDropzoneProps {
	onFile: (file: File) => void;
	onStartBlank: () => void;
	parseError: string | null;
}

export function ImportMetersDropzone({
	onFile,
	onStartBlank,
	parseError,
}: ImportMetersDropzoneProps) {
	const { t } = useTranslation();
	const [isDragging, setIsDragging] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragging(false);
			const dropped = e.dataTransfer.files[0];
			if (dropped) onFile(dropped);
		},
		[onFile],
	);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback(() => setIsDragging(false), []);

	return (
		<div className="flex flex-col gap-4 px-4 pb-4">
			<div className="flex items-center justify-between">
				<Label></Label>
				<a
					href="/CSV_EXAMPLE_METERS_IMPORT.csv"
					download
					className="text-xs text-muted-foreground underline hover:text-foreground"
				>
					{t("meterImport.downloadTemplate")}
				</a>
			</div>
			<button
				type="button"
				onClick={() => inputRef.current?.click()}
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				className={`flex flex-col items-center gap-2 rounded-md border-2 border-dashed px-4 py-10 text-center text-sm transition-colors ${
					isDragging
						? "border-primary bg-primary/5 text-primary"
						: "border-muted-foreground/25 text-muted-foreground hover:border-primary/50 hover:text-foreground"
				}`}
			>
				<UploadSimpleIcon aria-hidden="true" className="size-6" />
				<span>
					{isDragging ? t("meterImport.dragActive") : t("meterImport.dragDrop")}
				</span>
			</button>
			<input
				ref={inputRef}
				type="file"
				accept=".csv,text/csv"
				className="hidden"
				onChange={(e) => {
					const f = e.target.files?.[0];
					if (f) onFile(f);
					e.target.value = "";
				}}
			/>
			{parseError && (
				<div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
					<FileArrowUp aria-hidden="true" className="size-4 shrink-0" />
					<span>{t(`meterImport.parseError.${parseError}`)}</span>
				</div>
			)}
			<div className="flex items-center gap-3">
				<div className="h-px flex-1 bg-border" />
				<span className="text-xs text-muted-foreground">
					{t("meterImport.or")}
				</span>
				<div className="h-px flex-1 bg-border" />
			</div>
			<Button variant="outline" onClick={onStartBlank}>
				{t("meterImport.startBlank")}
			</Button>
		</div>
	);
}
